import { CanvassApi } from "./api";
import { type PeerLocation, type Position } from "./domain";

const sessionOperations = new WeakMap<object, Map<string, Promise<void>>>();

function enqueue(api: object, session: string, operation: () => Promise<void>): Promise<void> {
  let sessions = sessionOperations.get(api);
  if (!sessions) {
    sessions = new Map();
    sessionOperations.set(api, sessions);
  }
  const next = (sessions.get(session) ?? Promise.resolve()).catch(() => {}).then(operation);
  sessions.set(session, next);
  const finished = () => {
    if (sessions.get(session) === next) sessions.delete(session);
  };
  void next.then(finished, finished);
  return next;
}

/** Serialize withdrawal after the last publication so old requests cannot restore a location. */
export class SessionLocation {
  private active = true;
  private sharing = true;
  private watch: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private position: Position | null = null;
  private publishing = false;
  public constructor(
    private readonly api: Pick<CanvassApi, "publishLocation" | "clearLocation">,
    private readonly session: string | null,
    private readonly onPosition: (position: Position) => void,
    private readonly onPeers: (peers: readonly PeerLocation[]) => void,
    private readonly onError: (error: unknown) => void,
  ) {}

  public start(): void {
    if (!navigator.geolocation) {
      this.onError(new Error("Location is unavailable. Pan the map and use Search this area."));
      return;
    }
    this.watch = navigator.geolocation.watchPosition(
      (position) => {
        if (!this.active) return;
        this.position = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        this.onPosition(this.position);
        this.publish();
      },
      (error) => {
        if (this.active)
          this.onError(
            new Error(
              `${error.message} Pan the map and use Search this area, or enable location in your browser settings.`,
            ),
          );
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20_000 },
    );
    if (this.session !== null) this.timer = setInterval(() => this.publish(), 5000);
  }
  public setSharing(sharing: boolean): void {
    this.sharing = sharing;
    if (sharing) this.publish();
    else {
      this.onPeers([]);
      void this.clear();
    }
  }
  private publish(): void {
    if (!this.active || !this.sharing || !this.session || !this.position || this.publishing)
      return;
    this.publishing = true;
    const position = this.position;
    void enqueue(this.api, this.session, async () => {
      if (!this.active || !this.sharing || !this.session) return;
      try {
        const peers = await this.api.publishLocation(this.session, position);
        if (this.active && this.sharing) this.onPeers(peers);
      } catch (error) {
        if (this.active && this.sharing) this.onError(error);
      }
    }).finally(() => {
      this.publishing = false;
    });
  }
  private clear(): Promise<void> {
    if (!this.session) return Promise.resolve();
    const session = this.session;
    return enqueue(this.api, session, () => this.api.clearLocation(session)).catch(() => {
      // Server locations also expire. Revoked or unavailable sessions may reject withdrawal.
    });
  }
  public dispose(): Promise<void> {
    this.active = false;
    if (this.watch !== null) navigator.geolocation.clearWatch(this.watch);
    if (this.timer !== null) clearInterval(this.timer);
    this.position = null;
    this.onPeers([]);
    return this.clear();
  }
}
